import { router, usePage } from '@inertiajs/react';
import { Loader2, Music2, Plus, Search, SkipForward } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import type {
  Contributor,
  MusicState,
  MusicStats,
  NowPlayingTrack,
  QueueRow,
  RecentRow,
  SearchResult,
} from '@/lib/music-state';
import { cn } from '@/lib/utils';
import type { HackerMusicShow, SharedProps } from '@/types';

// /:event_slug/hacker/music — the 26-ar and 26-mx twins as one page (PORTING.md G5).
// The state is polled from the JSON route the Next component polled, at the same
// interval and with the same key (U4); only the "add song" server action became a
// controller mutation.

const fetcher = (url: string) =>
  fetch(url).then((r) => {
    if (!r.ok) throw new Error('fetch failed');
    return r.json();
  });

function useDebounced<T>(value: T, delay = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function formatMs(ms: number | null) {
  if (!ms) return '';
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function ago(date: string) {
  const d = new Date(date);
  const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
  if (seconds < 60) return `hace ${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `hace ${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h}h`;
  return `hace ${Math.floor(h / 24)}d`;
}

function displayName(opts: {
  hackerName: string | null;
  hackerGithub: string | null;
  discordUsername: string | null;
}) {
  if (opts.hackerGithub) {
    const parts = opts.hackerGithub.split('/');
    return `@${parts[parts.length - 1]}`;
  }
  if (opts.hackerName) return opts.hackerName;
  if (opts.discordUsername) return `@${opts.discordUsername}`;
  return 'anónimo';
}

export default function Show({
  initialState,
  titleFont,
  unavailable,
  eventSlug,
}: HackerMusicShow) {
  if (unavailable || initialState === null || eventSlug === null) {
    return <MusicUnavailable titleFont={titleFont} />;
  }

  return (
    <MusicBoard
      initialState={initialState}
      titleFont={titleFont}
      eventSlug={eventSlug}
    />
  );
}

// The edition's music bot is switched off (or has no Spotify behind it): the sidebar
// still links the page, so it explains itself rather than bouncing.
function MusicUnavailable({ titleFont }: { titleFont: string | null }) {
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto max-w-5xl px-4 py-10 md:py-12">
        <div className="space-y-8">
          <header className="flex items-center gap-3">
            <Music2 className="h-7 w-7 text-primary" />
            <h1
              className={cn(
                'font-bold text-2xl text-primary sm:text-3xl',
                titleFont,
              )}
            >
              MÚSICA
            </h1>
          </header>
          <div className="border-2 border-primary p-6 font-mono text-primary">
            <p className="font-bold">El bot de música no está activo ahora.</p>
            <p className="mt-2 text-primary/70 text-sm">
              Cuando lo prendamos, aquí podrás buscar canciones y sumarlas a la
              cola del evento.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function MusicBoard({
  initialState,
  titleFont,
  eventSlug,
}: {
  initialState: MusicState;
  titleFont: string | null;
  eventSlug: string;
}) {
  const { data: state } = useSWR<MusicState>(
    `/api/music/state?event=${encodeURIComponent(eventSlug)}`,
    fetcher,
    {
      fallbackData: initialState,
      refreshInterval: 5000,
      keepPreviousData: true,
    },
  );

  const view = state ?? initialState;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto max-w-5xl px-4 py-10 md:py-12">
        <div className="space-y-8">
          <header className="flex items-center gap-3">
            <Music2 className="h-7 w-7 text-primary" />
            <h1
              className={cn(
                'font-bold text-2xl text-primary sm:text-3xl',
                titleFont,
              )}
            >
              MÚSICA
            </h1>
          </header>

          <SearchAndAdd eventSlug={eventSlug} />

          <NowPlayingCard nowPlaying={view.nowPlaying} titleFont={titleFont} />

          <Tabs defaultValue="upcoming" className="w-full">
            <TabsList className="grid h-auto w-full max-w-md grid-cols-3 rounded-none border-2 border-primary bg-background p-0 font-mono text-primary">
              <TabsTrigger
                value="upcoming"
                className="rounded-none px-3 py-2 data-[state=active]:bg-primary data-[state=active]:text-background"
              >
                Próximas ({view.queue.length})
              </TabsTrigger>
              <TabsTrigger
                value="recent"
                className="rounded-none px-3 py-2 data-[state=active]:bg-primary data-[state=active]:text-background"
              >
                Recientes
              </TabsTrigger>
              <TabsTrigger
                value="stats"
                className="rounded-none px-3 py-2 data-[state=active]:bg-primary data-[state=active]:text-background"
              >
                Stats
              </TabsTrigger>
            </TabsList>

            <TabsContent value="upcoming" className="mt-4">
              <UpcomingList queue={view.queue} />
            </TabsContent>
            <TabsContent value="recent" className="mt-4">
              <RecentList recent={view.recent} />
            </TabsContent>
            <TabsContent value="stats" className="mt-4">
              <StatsPanel
                stats={view.stats}
                contributors={view.topContributors}
                titleFont={titleFont}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

// ---------------- Search ----------------

function SearchAndAdd({ eventSlug }: { eventSlug: string }) {
  const page = usePage();
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query, 250);
  const [pending, setPending] = useState(false);
  const [feedback, setFeedback] = useState<
    | { kind: 'success'; message: string }
    | { kind: 'error'; message: string }
    | null
  >(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const { data, isLoading } = useSWR<{ tracks: SearchResult[] }>(
    debounced.length >= 2
      ? `/api/music/search?event=${encodeURIComponent(eventSlug)}&q=${encodeURIComponent(debounced)}`
      : null,
    fetcher,
    { keepPreviousData: true },
  );

  const tracks = data?.tracks ?? [];

  function handleAdd(track: SearchResult) {
    setPendingId(track.id);
    setPending(true);
    setFeedback(null);
    router.post(
      `${page.url.split('?')[0]}/songs`,
      { spotifyTrackId: track.id, spotifyUri: track.uri },
      {
        preserveState: true,
        preserveScroll: true,
        onSuccess: (nextPage) => {
          const { flash } = nextPage.props as unknown as SharedProps;
          setFeedback({ kind: 'success', message: flash.notice ?? '' });
          setQuery('');
        },
        onError: (errors) => {
          const message = errors.song;
          setFeedback({
            kind: 'error',
            message: Array.isArray(message) ? message[0] : (message ?? ''),
          });
        },
        onFinish: () => {
          setPending(false);
          setPendingId(null);
        },
      },
    );
  }

  return (
    <section className="border-2 border-primary bg-background/80 p-4 backdrop-blur-sm">
      <div className="flex items-center gap-3 border-primary/20 border-b pb-3">
        <Search className="h-4 w-4 text-primary" />
        <h2 className="font-bold font-mono text-primary text-sm uppercase tracking-wide">
          Buscar y agregar
        </h2>
        <div className="ml-auto flex items-center gap-2">
          {isLoading && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-primary/60" />
          )}
        </div>
      </div>

      <div className="pt-3">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Bad Bunny, Tití me preguntó..."
          className="border-2 border-primary/30 bg-background font-mono text-primary placeholder:text-primary/40 focus-visible:border-primary focus-visible:ring-0"
        />
      </div>

      {feedback && (
        <p
          className={cn(
            'mt-3 font-mono text-sm',
            feedback.kind === 'success' ? 'text-primary' : 'text-destructive',
          )}
        >
          {feedback.message}
        </p>
      )}

      {tracks.length > 0 && (
        <ul className="mt-4 divide-y divide-primary/15 border-2 border-primary/30">
          {tracks.map((track) => {
            const isAdding = pending && pendingId === track.id;
            return (
              <li key={track.id} className="flex items-center gap-3 px-3 py-2">
                {track.albumArt ? (
                  <img
                    src={track.albumArt}
                    alt=""
                    className="h-10 w-10 flex-shrink-0 border border-primary/20 object-cover"
                  />
                ) : (
                  <div className="h-10 w-10 flex-shrink-0 border border-primary/20 bg-primary/10" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-mono text-primary text-sm">
                    {track.name}
                  </p>
                  <p className="truncate font-mono text-primary/60 text-xs">
                    {track.artists}
                  </p>
                </div>
                <span className="hidden font-mono text-primary/50 text-xs sm:inline">
                  {formatMs(track.durationMs)}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={pending}
                  onClick={() => handleAdd(track)}
                  className="h-8 border-2 border-primary font-mono text-primary hover:bg-primary hover:text-background"
                >
                  {isAdding ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Plus className="h-3.5 w-3.5" />
                  )}
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      {debounced.length >= 2 && !isLoading && tracks.length === 0 && (
        <p className="mt-4 font-mono text-primary/60 text-sm">
          Sin resultados.
        </p>
      )}
    </section>
  );
}

// ---------------- Now Playing ----------------

function NowPlayingCard({
  nowPlaying,
  titleFont,
}: {
  nowPlaying: NowPlayingTrack | null;
  titleFont: string | null;
}) {
  if (!nowPlaying) {
    return (
      <section className="border-2 border-primary/30 border-dashed bg-background/40 p-6 text-center">
        <p className="font-mono text-primary/60 text-sm">Nada sonando ahora.</p>
      </section>
    );
  }

  const score = nowPlaying.currentVoteScore;
  const addedBy = displayName({
    hackerName: nowPlaying.addedByHackerName,
    hackerGithub: nowPlaying.addedByHackerGithub,
    discordUsername: nowPlaying.addedByDiscordUsername,
  });

  return (
    <section className="border-2 border-primary bg-background/80 p-5 backdrop-blur-sm">
      <div className="flex items-start gap-4">
        {nowPlaying.albumArt ? (
          <img
            src={nowPlaying.albumArt}
            alt=""
            className="h-24 w-24 flex-shrink-0 border-2 border-primary object-cover"
          />
        ) : (
          <div className="h-24 w-24 flex-shrink-0 border-2 border-primary bg-primary/10" />
        )}
        <div className="min-w-0 flex-1 space-y-2">
          <div>
            <p className="font-bold font-mono text-primary/60 text-xs uppercase tracking-wider">
              Sonando ahora
            </p>
            <a
              href={nowPlaying.trackUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'block truncate font-bold text-primary text-xl hover:underline',
                titleFont,
              )}
            >
              {nowPlaying.trackName}
            </a>
            <p className="truncate font-mono text-primary/70 text-sm">
              {nowPlaying.trackArtists}
            </p>
          </div>
          <p className="font-mono text-primary/60 text-xs">
            agregada por <span className="text-primary">{addedBy}</span>
          </p>
          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            <ScoreBadge score={score} />
            <VoteBucketChip emoji="😍" count={undefined} muted />
            <VoteBucketChip emoji="👍" count={undefined} muted />
            <VoteBucketChip emoji="👎" count={undefined} muted />
            <VoteBucketChip emoji="🤮" count={undefined} muted />
            <span className="ml-1 font-mono text-primary/40 text-xs">
              skip @ -7
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function ScoreBadge({ score }: { score: number }) {
  const sign = score > 0 ? '+' : '';
  const tone =
    score > 0
      ? 'border-primary bg-primary text-background'
      : score < 0
        ? 'border-destructive text-destructive'
        : 'border-primary/30 text-primary/70';
  return (
    <Badge
      variant="outline"
      className={cn('rounded-none border-2 font-mono', tone)}
    >
      {sign}
      {score}
    </Badge>
  );
}

function VoteBucketChip({
  emoji,
  count,
  muted,
}: {
  emoji: string;
  count: number | undefined;
  muted?: boolean;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 border border-primary/30 px-1.5 py-0.5 font-mono text-xs',
        muted ? 'text-primary/40' : 'text-primary',
      )}
    >
      <span>{emoji}</span>
      <span>{count ?? '–'}</span>
    </span>
  );
}

// ---------------- Upcoming ----------------

function UpcomingList({ queue }: { queue: QueueRow[] }) {
  if (queue.length === 0) {
    return (
      <div className="border-2 border-primary/30 border-dashed bg-background/40 p-6 text-center">
        <p className="font-mono text-primary/60 text-sm">
          La cola está vacía. Buscá una canción arriba para sumarla.
        </p>
      </div>
    );
  }

  let cumulative = 0;
  return (
    <ul className="divide-y divide-primary/15 border-2 border-primary bg-background/80 backdrop-blur-sm">
      {queue.map((row, idx) => {
        const eta = cumulative;
        cumulative += row.durationMs ?? 0;
        const requester = displayName({
          hackerName: row.requestedByHackerName,
          hackerGithub: row.requestedByHackerGithub,
          discordUsername: row.requestedByDiscordUsername,
        });
        return (
          <li key={row.id} className="flex items-center gap-3 px-3 py-2.5">
            <span className="w-5 font-mono text-primary/40 text-xs">
              {idx + 1}
            </span>
            {row.albumArt ? (
              <img
                src={row.albumArt}
                alt=""
                className="h-10 w-10 flex-shrink-0 border border-primary/20 object-cover"
              />
            ) : (
              <div className="h-10 w-10 flex-shrink-0 border border-primary/20 bg-primary/10" />
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate font-mono text-primary text-sm">
                {row.trackName}
              </p>
              <p className="truncate font-mono text-primary/60 text-xs">
                {row.trackArtists}
              </p>
            </div>
            <div className="hidden text-right sm:block">
              <p className="font-mono text-primary text-xs">{requester}</p>
              <p className="font-mono text-primary/50 text-xs">
                ~{Math.round(eta / 60000)} min
              </p>
            </div>
          </li>
        );
      })}
      <li className="bg-primary/5 px-3 py-2 font-mono text-primary/50 text-xs">
        Spotify puede reordenar — esta es la orden de pedido.
      </li>
    </ul>
  );
}

// ---------------- Recent ----------------

function RecentList({ recent }: { recent: RecentRow[] }) {
  if (recent.length === 0) {
    return (
      <div className="border-2 border-primary/30 border-dashed bg-background/40 p-6 text-center">
        <p className="font-mono text-primary/60 text-sm">
          Aún no se reprodujo nada.
        </p>
      </div>
    );
  }
  return (
    <ul className="divide-y divide-primary/15 border-2 border-primary bg-background/80 backdrop-blur-sm">
      {recent.map((row) => {
        const requester = displayName({
          hackerName: row.addedByHackerName,
          hackerGithub: null,
          discordUsername: row.addedByDiscordUsername,
        });
        const score = row.finalVoteScore ?? 0;
        const hasBuckets = row.voterCount > 0;
        return (
          <li key={row.id} className="flex items-center gap-3 px-3 py-2.5">
            {row.albumArt ? (
              <img
                src={row.albumArt}
                alt=""
                className="h-10 w-10 flex-shrink-0 border border-primary/20 object-cover"
              />
            ) : (
              <div className="h-10 w-10 flex-shrink-0 border border-primary/20 bg-primary/10" />
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate font-mono text-primary text-sm">
                {row.trackName}
              </p>
              <p className="truncate font-mono text-primary/60 text-xs">
                {row.trackArtists} · {requester}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-1.5">
                {row.wasSkipped && (
                  <SkipForward className="h-3.5 w-3.5 text-destructive" />
                )}
                <ScoreBadge score={score} />
              </div>
              <div className="flex items-center gap-2 font-mono text-primary/50 text-xs">
                {hasBuckets && (
                  <span className="hidden sm:inline">
                    😍 {row.loveCount} 👍 {row.likeCount} 👎 {row.dislikeCount}{' '}
                    🤮 {row.hateCount}
                  </span>
                )}
                <span>{ago(row.playedAt)}</span>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// ---------------- Stats ----------------

function StatsPanel({
  stats,
  contributors,
  titleFont,
}: {
  stats: MusicStats;
  contributors: Contributor[];
  titleFont: string | null;
}) {
  const skipRate = useMemo(() => {
    if (!stats || stats.totalTracks === 0) return 0;
    return Math.round((stats.totalSkipped / stats.totalTracks) * 100);
  }, [stats]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <StatTile
          label="Reproducidas"
          value={stats?.totalTracks ?? 0}
          titleFont={titleFont}
        />
        <StatTile
          label="Skip rate"
          value={`${skipRate}%`}
          titleFont={titleFont}
        />
        <StatTile
          label="Score prom."
          titleFont={titleFont}
          value={
            stats?.avgVoteScore == null
              ? '–'
              : (Math.round(stats.avgVoteScore * 10) / 10).toString()
          }
        />
      </div>
      <div className="border-2 border-primary bg-background/80 p-4 backdrop-blur-sm">
        <p className="font-bold font-mono text-primary/60 text-xs uppercase tracking-wide">
          Top contributors
        </p>
        {contributors.length === 0 ? (
          <p className="mt-2 font-mono text-primary/50 text-sm">
            Nadie todavía.
          </p>
        ) : (
          <ol className="mt-3 space-y-1.5">
            {contributors.map((c, idx) => {
              const name = c.github
                ? `@${c.github.split('/').pop()}`
                : c.hackerName;
              return (
                <li
                  key={c.hackerId}
                  className="flex items-center justify-between font-mono text-sm"
                >
                  <span className="text-primary">
                    <span className="text-primary/40">{idx + 1}.</span> {name}
                  </span>
                  <span className="text-primary/60">{c.trackCount}</span>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}

function StatTile({
  label,
  value,
  titleFont,
}: {
  label: string;
  value: string | number;
  titleFont: string | null;
}) {
  return (
    <div className="border-2 border-primary bg-background/80 p-3 backdrop-blur-sm">
      <p className="font-mono text-primary/60 text-xs uppercase tracking-wide">
        {label}
      </p>
      <p className={cn('font-bold text-2xl text-primary', titleFont)}>
        {value}
      </p>
    </div>
  );
}
